import { Flex } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import Icon from "../../../../../../Icon"

import chipStyles from "../chips.module.less"
import styles from "./cardSelect.module.less"

// Single-select rendered as icon cards / icon rows / chips, instead of a plain
// dropdown, so the higher-signal Configure questions read as something the merchant
// scans and clicks. One control, three layouts chosen per question via `variant`:
//   • 'cards' grid  - icon + short label tiles (e.g. subscription signup flow)
//   • 'rows'        - icon + title + one-line description rows (e.g. consent model)
//   • 'pills'       - the SAME default chip/tag the rest of Configure uses (chip-multi),
//                     just single-select (one chip primary, the rest default).
//
// It keeps the same value contract as the antd Select it replaces (value = the
// selected option's value, onChange(value)), so QuestionField can swap it in for a
// `select` question with no other change. Icons come ONLY from the app Icon registry
// (src/Icon) and are forced to the 20px system size; every colour is a theme token so
// each state is correct in light and dark. Radio semantics: re-clicking the selected
// option does not clear it (the section Reset handles clearing).
const CardSelect = ({
  layout = "cards",
  value,
  options = [],
  onChange,
  ariaLabel,
  status,
  t: tProp
}) => {
  const { t: tHook } = useTranslation()
  const t = tProp || tHook

  // Pills reuse the exact default tag styling from chip-multi (.dq-chip), so binary /
  // short answers look identical to every other tag in Configure, just single-select.
  if (layout === "pills") {
    return (
      <Flex
        wrap="wrap"
        gap={8}
        className={chipStyles.chipRow}
        role="radiogroup"
        aria-label={ariaLabel ? t(ariaLabel) : undefined}
      >
        {options.map((opt) => {
          const checked = value === opt.value
          return (
            <Button
              key={opt.value}
              role="radio"
              aria-checked={checked}
              type={checked ? "primary" : "default"}
              className={`${chipStyles.chip} ${checked ? chipStyles.chipOn : ""}`}
              onClick={() => onChange(opt.value)}
              text={t(opt.label)}
            />
          )
        })}
      </Flex>
    )
  }

  return (
    <div
      className={`${styles.cardsel} ${layout === "rows" ? styles.cardselRows : styles.cardselCards}${status === "error" ? ` ${styles.cardselError}` : ""}`}
      role="radiogroup"
      aria-label={ariaLabel ? t(ariaLabel) : undefined}
    >
      {options.map((opt) => {
        const checked = value === opt.value
        return (
          <button
            type="button"
            key={opt.value}
            role="radio"
            aria-checked={checked}
            className={`${styles.cardselItem}${checked ? ` ${styles.cardselItemOn}` : ""}`}
            onClick={() => onChange(opt.value)}
          >
            <span className={styles.cardselRadio} aria-hidden="true" />
            {opt.icon ? (
              <span className={styles.cardselIcon}>
                <Icon type={opt.icon} />
              </span>
            ) : null}
            <span className={styles.cardselBody}>
              <span className={styles.cardselLabel}>{t(opt.label)}</span>
              {layout === "rows" && opt.description ? (
                <span className={styles.cardselDesc}>{t(opt.description)}</span>
              ) : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default CardSelect
